import {
	ComponentRef,
	Directive,
	ElementRef,
	EventEmitter,
	HostBinding,
	HostListener,
	Inject,
	inject,
	Input,
	OnDestroy,
	Output,
	TemplateRef,
	Type,
	ViewContainerRef
} from '@angular/core';
import {
	ConnectedPosition,
	FlexibleConnectedPositionStrategy,
	Overlay,
	OverlayRef,
	ScrollStrategy
} from '@angular/cdk/overlay';
import { ComponentPortal, TemplatePortal } from '@angular/cdk/portal';
import { filter, fromEvent, Subject } from 'rxjs';
import { coerceBooleanProperty, coerceNumberProperty } from '@angular/cdk/coercion';
import { takeUntil } from 'rxjs/operators';
import { GoCdkPopoverContentComponent, PopoverPosition, PopoverTrigger, PopoverScrollStrategy } from '@go-modules/directives/go-cdk-popover/go-cdk-popover-content/go-cdk-popover-content.component';

export const TABBABLE_SELECTOR = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])';
const MATERIAL_TOOLTIP_SELECTOR = '.mat-mdc-tooltip-panel .mat-mdc-tooltip, .mat-tooltip-panel .mat-tooltip';

@Directive({
	selector: '[goCdkPopover]',
	standalone: true,
	exportAs: 'goCdkPopover'
})
export class GoCdkPopoverDirective implements OnDestroy {

	@HostBinding('attr.aria-describedby') private get ariaDescribedBy () { return this.isOpen() ? this.overlayId : null; }
	@HostBinding('attr.aria-expanded') private get ariaExpanded () { return this.isOpen(); }
	@HostBinding('attr.aria-haspopup') private ariaHasPopup = 'true';

	private overlay = inject(Overlay);
	private elementRef = inject(ElementRef);
	private viewContainerRef = inject(ViewContainerRef);
	private destroy$ = new Subject<void>();
	private positionStrategy!: FlexibleConnectedPositionStrategy;
	private portal?: ComponentPortal<GoCdkPopoverContentComponent> | TemplatePortal;
	private touchTimeout?: number;
	private closeTimeout?: number;
	private openTimeout?: number;
	private hoverCheckTimeout?: number;
	private tabbableElements?: HTMLElement[];
	private suppressNextFocusOpen: boolean = false;

	public overlayId = `go-cdk-popover-${Math.random().toString(36).substring(2, 11)}`;
	public contentComponentRef?: ComponentRef<GoCdkPopoverContentComponent>;
	public overlayRef?: OverlayRef;
	public touchTimeoutDelay = 250;
	public hoverTimeoutDelay = 100;

	@Input('goCdkPopover') public content: string | TemplateRef<any> | Type<any> = '';
	@Input('goCdkPopoverContext') public context: any = {};
	@Input('goCdkPopoverPosition') public position: PopoverPosition = 'below';
	@Input('goCdkPopoverTrigger') public trigger: PopoverTrigger = 'hover';
	@Input('goCdkPopoverPanelClass') public panelClass: string | string[] = '';
	@Input('goCdkPopoverScrollStrategy') public scrollStrategy: PopoverScrollStrategy = 'reposition';
	@Input('goCdkPopoverOffsetX') public offsetX = 0;
	@Input('goCdkPopoverOffsetY') public offsetY = 8;
	@Input('goCdkPopoverCloseButtonAriaLabel') public closeButtonAriaLabel = 'Close popover';
	@Input('goCdkPopoverBgColor') public bgColor?: string;
	@Input('goCdkPopoverMaxHeight') public maxHeight?: number | string;

	// https://v14.material.angular.dev/cdk/coercion/overview
	@Input('goCdkPopoverDisabled')
	public get disabled (): boolean { return this._disabled; }
	public set disabled (value: any) { this._disabled = coerceBooleanProperty(value); }
	private _disabled = false;

	@Input('goCdkPopoverShowDelay')
	public get showDelay (): number { return this._showDelay; }
	public set showDelay (value: any) { this._showDelay = coerceNumberProperty(value); }
	private _showDelay = 0;

	@Input('goCdkPopoverHideDelay')
	public get hideDelay (): number { return this._hideDelay; }
	public set hideDelay (value: any) { this._hideDelay = coerceNumberProperty(value); }
	private _hideDelay = 0;

	@Input('goCdkPopoverShowCloseButton')
	public get showCloseButton (): boolean { return this._showCloseButton; }
	public set showCloseButton (value: any) { this._showCloseButton = coerceBooleanProperty(value); }
	private _showCloseButton = false;

	@Input('goCdkPopoverShowArrow')
	public get showArrow (): boolean { return this._showArrow; }
	public set showArrow (value: any) { this._showArrow = coerceBooleanProperty(value); }
	private _showArrow = false;

	@Input('goCdkPopoverForceFocusTrap')
	public get forceFocusTrap (): boolean { return this._forceFocusTrap; }
	public set forceFocusTrap (value: any) { this._forceFocusTrap = coerceBooleanProperty(value); }
	private _forceFocusTrap = false;

	@Input('goCdkPopoverShadow')
	public get shadow (): boolean { return this._shadow; }
	public set shadow (value: any) { this._shadow = coerceBooleanProperty(value); }
	private _shadow = false;

	// Output events
	@Output('goCdkPopoverOpened') public opened = new EventEmitter<void>();
	@Output('goCdkPopoverClosed') public closed = new EventEmitter<void>();

	@HostListener('click', ['$event'])
	private onClick (event: MouseEvent) {
		if (this.trigger === 'click' && !this.disabled) {
			event.stopPropagation();
			this.toggle();
		}
	}

	@HostListener('mouseenter')
	private onMouseEnter (): void {
		if (this.trigger === 'hover' && !this.disabled) {
			this.show();
		}
	}

	@HostListener('mouseleave')
	private onMouseLeave (): void {
		if (this.trigger !== 'hover') return;

		// Cancel any pending open timeout immediately
		if (this.openTimeout) {
			clearTimeout(this.openTimeout);
			this.openTimeout = undefined;
		}
		if (this.contentComponentRef) {
			this.handleHoverableMouseLeave();
		} else {
			this.hide();
		}
	}

	@HostListener('touchstart')
	private onTouchStart (): void {
		if (!this.disabled) {
			// use a short delay to allow for long press and prevent accidental taps
			this.touchTimeout = window.setTimeout(() => {
				this.show();
			}, this.touchTimeoutDelay);
		}
	}

	@HostListener('touchend')
	private onTouchEnd (): void {
		this.cancelTouchTimeout();
	}

	@HostListener('touchcancel')
	private onTouchCancel (): void {
		this.cancelTouchTimeout();
	}

	@HostListener('touchmove')
	private onTouchMove (): void {
		this.cancelTouchTimeout();
	}

	@HostListener('focus')
	private onFocus (): void {
		if (this.disabled) return;
		if (this.trigger === 'hover') {
			if (this.suppressNextFocusOpen) return;
			this.show();
		}
	}

	@HostListener('blur')
	private onBlur (): void {
		this.suppressNextFocusOpen = false;
	}

	constructor (@Inject('Window') private window: Window) {}

	public isInteractive (): boolean {
		if (this.overlayRef?.hasAttached()) {
			return this.getTabbableElements(this.overlayRef).length > 0;
		}
		return false;
	}

	public get shouldTrapFocus (): boolean {
		return this.forceFocusTrap;
	}

	public ngOnDestroy (): void {
		this.destroy$.next();
		this.destroy$.complete();
		this.close();
		this.clearAllTimeouts();
	}

	public show (delay = this.showDelay): void {
		if (this.disabled || this.isOpen() || !this.content) {
			return;
		}

		this.clearAllTimeouts();

		if (delay > 0) {
			this.openTimeout = this.window.setTimeout(() => {
				this.openTimeout = undefined;
				this.show(0);
			}, delay);
			return;
		}

		this.overlayRef = this.createOverlay();
		this.portal = this.createPortal();

		// set z-index to be higher than legacy 1050 z-index overlays
		const overlayContainer = document.querySelector('.cdk-overlay-container');
		overlayContainer?.classList.add('popover-high-z');

		this.contentComponentRef = this.overlayRef.attach(this.portal);
		this.configureContent(this.contentComponentRef);
		this.setupViewportConstraints();

		// Calculate initial arrow offset after content is attached
		if (this.showArrow && this.contentComponentRef) {
			this.window.requestAnimationFrame(() => {
				const arrowPosition = this.contentComponentRef?.instance.arrowPosition || `arrow-${this.position}`;
				this.calculateAndSetArrowOffset(this.contentComponentRef!, arrowPosition);
			});
		}

		this.opened.emit();
	}

	public hide (delay = this.hideDelay): void {
		if (this.openTimeout) {
			this.window.clearTimeout(this.openTimeout);
			this.openTimeout = undefined;
		}

		if (this.closeTimeout) {
			this.window.clearTimeout(this.closeTimeout);
			this.closeTimeout = undefined;
		}

		if (!this.isOpen()) {
			return;
		}

		if (delay > 0) {
			this.closeTimeout = this.window.setTimeout(() => {
				this.closeTimeout = undefined;
				this.hide(0);
			}, delay);
			return;
		}

		this.close();
	}

	public toggle (): void {
		if (this.isOpen()) {
			this.hide();
		} else {
			this.show();
		}
	}

	public isOpen (): boolean {
		return !!this.overlayRef?.hasAttached();
	}

	private close (): void {
		const overlayContainer = document.querySelector('.cdk-overlay-container');
		overlayContainer?.classList.remove('popover-high-z');
		if (this.contentComponentRef) {
			this.contentComponentRef.destroy();
			this.contentComponentRef = undefined;
		}

		if (this.overlayRef) {
			this.overlayRef.dispose();
			this.overlayRef = undefined;
		}

		this.portal = undefined;
		this.tabbableElements = undefined;
		this.suppressNextFocusOpen = false;
		this.closed.emit();
	}

	private clearAllTimeouts (): void {
		if (this.touchTimeout) {
			this.window.clearTimeout(this.touchTimeout);
			this.touchTimeout = undefined;
		}
		if (this.closeTimeout) {
			this.window.clearTimeout(this.closeTimeout);
			this.closeTimeout = undefined;
		}
		if (this.openTimeout) {
			this.window.clearTimeout(this.openTimeout);
			this.openTimeout = undefined;
		}
		if (this.hoverCheckTimeout) {
			this.window.clearTimeout(this.hoverCheckTimeout);
			this.hoverCheckTimeout = undefined;
		}
	}

	private cancelTouchTimeout (): void {
		if (this.touchTimeout) {
			this.window.clearTimeout(this.touchTimeout);
			this.touchTimeout = undefined;
		}
		if (this.isOpen() && this.trigger === 'hover') {
			this.hide(500); // Longer delay on touch devices
		}
	}

	private handleHoverableMouseLeave (): void {
		const contentEl = this.contentComponentRef?.location.nativeElement;
		if (!contentEl) return;

		this.hoverCheckTimeout = this.window.setTimeout(() => {
			this.hoverCheckTimeout = undefined;
			if (!contentEl.matches(':hover') && !this.elementRef.nativeElement.matches(':hover')) {
				this.hide();
			}
		}, this.hoverTimeoutDelay);
	}

	private isTopmostFocusedContainer (): boolean {
		const activeElement = this.window.document.activeElement;
		return !!(activeElement && this.overlayRef?.overlayElement.contains(activeElement));
	}

	private getTabbableElements (overlayRef: OverlayRef): HTMLElement[] {
		return Array.from(
			overlayRef.overlayElement.querySelectorAll(TABBABLE_SELECTOR)
		) as HTMLElement[];
	}

	private createOverlay (): OverlayRef {
		if (this.overlayRef) {
			return this.overlayRef;
		}

		this.positionStrategy = this.getPositionStrategy();
		const scrollStrategy = this.getScrollStrategy();

		const overlayRef = this.overlay.create({
			positionStrategy: this.positionStrategy,
			scrollStrategy,
			hasBackdrop: this.trigger === 'click',
			backdropClass: 'cdk-overlay-transparent-backdrop',
			panelClass: this.panelClass
		});

		this.setupTriggerAndOverlayEvents(overlayRef);
		return overlayRef;
	}

	private setupTriggerAndOverlayEvents (overlayRef: OverlayRef): void {
		if (this.trigger === 'hover') {
			// Don't rely on backdropClick for hover popovers. Backdrop messes with pointer events
			fromEvent<PointerEvent>(this.window.document, 'pointerdown')
				.pipe(
					takeUntil(this.destroy$),
					takeUntil(overlayRef.detachments()),
					// click outside both trigger and overlay
					filter((ev) => {
						const t = ev.target as Node;
						return !this.elementRef.nativeElement.contains(t) &&
							!overlayRef.overlayElement.contains(t);
					})
				)
				.subscribe(() => this.hide());
		} else {
			overlayRef.backdropClick()
				.pipe(takeUntil(this.destroy$), takeUntil(overlayRef.detachments()))
				.subscribe(() => this.hide());
		}

		this.setupKeyboardEvents(overlayRef);
	}

	private setupKeyboardEvents (overlayRef: OverlayRef): void {
		fromEvent<KeyboardEvent>(overlayRef.overlayElement, 'keydown', { capture: true })
			.pipe(takeUntil(this.destroy$), takeUntil(overlayRef.detachments()))
			.subscribe((event: KeyboardEvent) => {
				this.handleOverlayKeyboardEvent(event, overlayRef);
			});

		fromEvent<KeyboardEvent>(this.elementRef.nativeElement, 'keydown', { capture: true })
			.pipe(takeUntil(this.destroy$), takeUntil(overlayRef.detachments()))
			.subscribe((event) => {
				this.handleTriggerKeyboardEvent(event, overlayRef);
			});
	}

	private handleTriggerKeyboardEvent (event: KeyboardEvent, overlayRef: OverlayRef): void {
		if (event.key === 'Escape') {
			if (this.hasVisibleMaterialTooltip()) {
				return;
			}

			event.preventDefault();
			event.stopPropagation();
			this.hide(0);
			return;
		}

		if (event.key === 'Tab') {
			const tabbables = this.getTabbableElements(overlayRef);
			if (event.shiftKey) {
				// Shift+Tab off the trigger -> close
				this.hide(0);
				return;
			}

			// Forward Tab:
			if (tabbables.length > 0) {
				const first = tabbables[0];
				if (first) {
					event.preventDefault(); // don’t skip past the overlay
					first.focus();
				}
			} else {
				// No interactive content -> close and let Tab proceed
				this.hide(0);
			}
		}
	}

	private handleOverlayKeyboardEvent (event: KeyboardEvent, overlayRef: OverlayRef): void {
		if (event.key === 'Escape') {
			if (this.hasVisibleMaterialTooltip()) {
				return;
			}

			if (this.isTopmostFocusedContainer()) {
				event.preventDefault();
				event.stopPropagation();
				this.hide();
				this.suppressNextFocusOpen = true;
				this.elementRef.nativeElement.focus();
			}
		}

		if (event.key === 'Tab') {
			const focusedElement = this.window.document.activeElement;
			if (!focusedElement || !this.overlayRef?.overlayElement.contains(focusedElement)) {
				return;
			}

			this.tabbableElements = this.getTabbableElements(overlayRef);
			const isShiftTab = event.shiftKey;
			const firstTabbable = this.tabbableElements[0];
			const lastTabbable = this.tabbableElements[this.tabbableElements.length - 1];

			if (isShiftTab && focusedElement === firstTabbable) {
				// Shift+Tab on first element - close and focus previous element
				event.preventDefault();
				this.hide();
				const prevElement = this.getPreviousTabbableElement();
				if (prevElement) {
					prevElement.focus();
				}
			} else if (!isShiftTab && focusedElement === lastTabbable) {
				// Tab on last element - close and focus next element
				event.preventDefault();
				this.hide();
				const nextElement = this.getNextTabbableElement();
				if (nextElement) {
					nextElement.focus();
				}
			}
		}
	}

	private hasVisibleMaterialTooltip (): boolean {
		return this.window.document.querySelector(MATERIAL_TOOLTIP_SELECTOR) != null;
	}

	private getNextTabbableElement (): HTMLElement | null {
		const tabbableElements = Array.from(this.window.document.querySelectorAll(TABBABLE_SELECTOR)) as HTMLElement[];
		const visibleTabbableElements = tabbableElements.filter((el) => this.isElementVisible(el));
		const triggerIndex = visibleTabbableElements.indexOf(this.elementRef.nativeElement);

		return triggerIndex >= 0 && triggerIndex < visibleTabbableElements.length - 1
			? visibleTabbableElements[triggerIndex + 1]
			: null;
	}

	private getPreviousTabbableElement (): HTMLElement | null {
		const tabbableElements = Array.from(this.window.document.querySelectorAll(TABBABLE_SELECTOR)) as HTMLElement[];
		const visibleTabbableElements = tabbableElements.filter((el) => this.isElementVisible(el));
		const triggerIndex = visibleTabbableElements.indexOf(this.elementRef.nativeElement);

		return triggerIndex > 0
			? visibleTabbableElements[triggerIndex - 1]
			: null;
	}

	private isElementVisible (el: HTMLElement): boolean {
		const style = this.window.getComputedStyle(el);
		return style.display !== 'none' &&
			style.visibility !== 'hidden' &&
			!el.hasAttribute('disabled') &&
			el.tabIndex >= 0 &&
			el.getClientRects().length > 0;
	}

	private createPortal (): ComponentPortal<GoCdkPopoverContentComponent> | TemplatePortal {
		return this.portal ? this.portal : new ComponentPortal(GoCdkPopoverContentComponent, this.viewContainerRef);
	}

	private configureContent (contentComponentRef: ComponentRef<GoCdkPopoverContentComponent>): void {
		const instance = contentComponentRef.instance;
		contentComponentRef.setInput('id', this.overlayId);
		contentComponentRef.setInput('context', this.context);
		contentComponentRef.setInput('panelClass', this.panelClass);
		contentComponentRef.setInput('showCloseButton', this.showCloseButton);
		contentComponentRef.setInput('closeButtonAriaLabel', this.closeButtonAriaLabel);
		contentComponentRef.setInput('showArrow', this.showArrow);
		contentComponentRef.setInput('arrowPosition', `arrow-${this.position}`);
		contentComponentRef.setInput('arrowOffsetX', 0);
		contentComponentRef.setInput('arrowOffsetY', 0);
		contentComponentRef.setInput('isClickTriggered', this.trigger === 'click');
		contentComponentRef.setInput('isInteractive', this.isInteractive());
		contentComponentRef.setInput('shouldTrapFocus', this.shouldTrapFocus);
		contentComponentRef.setInput('bgColor', this.bgColor);
		contentComponentRef.setInput('shadow', this.shadow);
		contentComponentRef.setInput('maxContentHeight', this.maxHeight);

		// Set content based on type
		if (this.content instanceof TemplateRef) {
			contentComponentRef.setInput('contentTemplate', this.content);
		} else if (typeof this.content === 'string') {
			contentComponentRef.setInput('contentText', this.content);
		} else {
			contentComponentRef.setInput('contentComponent', this.content);
		}

		instance.updateContent();
		if (this.showCloseButton) {
			instance.closed
				.pipe(takeUntil(this.destroy$), takeUntil(this.overlayRef.detachments()))
				.subscribe(() => {
					this.hide();
					this.suppressNextFocusOpen = true;
					this.elementRef.nativeElement.focus();
				});
		}

		if (this.trigger === 'hover') {
			this.setupContentHoverEvents(contentComponentRef);
		}

		this.positionStrategy.positionChanges
			.pipe(takeUntil(this.destroy$), takeUntil(this.overlayRef.detachments()))
			.subscribe((change) => {
				const newArrow = this.connectionPairToArrow(change.connectionPair);
				if (contentComponentRef &&
					contentComponentRef.instance.arrowPosition !== newArrow) {
					contentComponentRef.setInput('arrowPosition', newArrow);
					contentComponentRef.instance.updateContent();
				}

				// Calculate arrow offset to keep it centered on opening element
				if (this.showArrow && this.overlayRef?.overlayElement) {
					this.calculateAndSetArrowOffset(contentComponentRef, newArrow);
				}
			});
	}

	private connectionPairToArrow (pair: ConnectedPosition): string {
		// Horizontal
		if (pair.overlayX === 'start' && pair.originX === 'end') {
			return this.position === 'right' ? 'arrow-right' : 'arrow-after';
		}
		if (pair.overlayX === 'end' && pair.originX === 'start') {
			return this.position === 'left' ? 'arrow-left' : 'arrow-before';
		}

		// Vertical
		if (pair.overlayY === 'top' && pair.originY === 'bottom') return 'arrow-below';
		return 'arrow-above';
	}

	private setupContentHoverEvents (contentComponentRef: ComponentRef<GoCdkPopoverContentComponent>): void {
		const contentEl = contentComponentRef.location.nativeElement;
		fromEvent<MouseEvent>(contentEl, 'mouseenter')
			.pipe(takeUntil(this.destroy$), takeUntil(this.overlayRef.detachments()))
			.subscribe(() => {
				if (this.closeTimeout) {
					this.window.clearTimeout(this.closeTimeout);
					this.closeTimeout = undefined;
				}
				if (this.hoverCheckTimeout) {
					this.window.clearTimeout(this.hoverCheckTimeout);
					this.hoverCheckTimeout = undefined;
				}
			});

		fromEvent<MouseEvent>(contentEl, 'mouseleave')
			.pipe(takeUntil(this.destroy$), takeUntil(this.overlayRef.detachments()))
			.subscribe(() => {
				if (!this.elementRef.nativeElement.matches(':hover')) {
					this.hide();
				}
			});
	}

	private getPositionStrategy (): FlexibleConnectedPositionStrategy {
		const positions = this.getPositions();
		return this.overlay.position()
			.flexibleConnectedTo(this.elementRef)
			.withPositions(positions)
			.withViewportMargin(8)
			.withFlexibleDimensions(false)
			.withPush(true)
			.withLockedPosition(false);
	}

	private getPositions (): ConnectedPosition[] {
		const offsetX = this.offsetX;
		const offsetY = this.offsetY;
		const arrowOffset = this.showArrow ? 12 : 0;

		const positions: Record<PopoverPosition, ConnectedPosition[]> = {
			above: [
				{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -(offsetY + arrowOffset) },
				{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: offsetY + arrowOffset }
			],
			below: [
				{ originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: offsetY + arrowOffset },
				{ originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -(offsetY + arrowOffset) }
			],
			before: [
				{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -(offsetX + arrowOffset) },
				{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: offsetX + arrowOffset }
			],
			after: [
				{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: offsetX + arrowOffset },
				{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -(offsetX + arrowOffset) }
			],
			left: [
				{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -(offsetX + arrowOffset) },
				{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: offsetX + arrowOffset }
			],
			right: [
				{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: offsetX + arrowOffset },
				{ originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -(offsetX + arrowOffset) }
			]
		};

		return positions[this.position] || positions.below;
	}

	private getScrollStrategy (): ScrollStrategy {
		switch (this.scrollStrategy) {
			case 'noop':
				return this.overlay.scrollStrategies.noop();
			case 'reposition':
				return this.overlay.scrollStrategies.reposition();
			case 'close':
			default:
				return this.overlay.scrollStrategies.close();
		}
	}

	private setupViewportConstraints (): void {
		if (!this.overlayRef || !this.positionStrategy) {
			return;
		}

		// Ensure overlay never goes outside viewport boundaries
		this.positionStrategy.positionChanges
			.pipe(takeUntil(this.destroy$), takeUntil(this.overlayRef.detachments()))
			.subscribe(() => {
				if (this.overlayRef?.overlayElement) {
					this.constrainToViewport(this.overlayRef.overlayElement);
				}
			});

		fromEvent(this.window, 'resize')
			.pipe(takeUntil(this.destroy$), takeUntil(this.overlayRef.detachments()))
			.subscribe(() => {
				if (this.overlayRef?.overlayElement) {
					this.constrainToViewport(this.overlayRef.overlayElement);
				}
			});
	}

	private calculateAndSetArrowOffset (
		contentComponentRef: ComponentRef<GoCdkPopoverContentComponent>,
		arrowPosition: string
	): void {
		if (!this.overlayRef?.overlayElement || !this.contentComponentRef) {
			return;
		}

		this.window.requestAnimationFrame(() => {
			const popoverPanel = contentComponentRef.location.nativeElement.querySelector('.go-cdk-popover-panel') as HTMLElement;
			if (!popoverPanel) {
				return;
			}

			const triggerElement = this.elementRef.nativeElement;

			const panelRect = popoverPanel.getBoundingClientRect();
			const triggerRect = triggerElement.getBoundingClientRect();

			const panelCenterX = panelRect.left + panelRect.width / 2;
			const panelCenterY = panelRect.top + panelRect.height / 2;
			const triggerCenterX = triggerRect.left + triggerRect.width / 2;
			const triggerCenterY = triggerRect.top + triggerRect.height / 2;

			let arrowOffsetX = 0;
			let arrowOffsetY = 0;

			if (arrowPosition === 'arrow-above' || arrowPosition === 'arrow-below') {
				arrowOffsetX = triggerCenterX - panelCenterX;
			} else if (arrowPosition === 'arrow-before' || arrowPosition === 'arrow-after' ||
				arrowPosition === 'arrow-left' || arrowPosition === 'arrow-right') {
				arrowOffsetY = triggerCenterY - panelCenterY;
			}

			const maxOffsetX = panelRect.width / 2 - 24;
			const maxOffsetY = panelRect.height / 2 - 24;

			arrowOffsetX = Math.max(-maxOffsetX, Math.min(maxOffsetX, arrowOffsetX));
			arrowOffsetY = Math.max(-maxOffsetY, Math.min(maxOffsetY, arrowOffsetY));

			contentComponentRef.setInput('arrowOffsetX', arrowOffsetX);
			contentComponentRef.setInput('arrowOffsetY', arrowOffsetY);
			contentComponentRef.instance.updateContent();
		});
	}

	private constrainToViewport (overlayElement: HTMLElement): void {
		this.window.requestAnimationFrame(() => {
			const rect = overlayElement.getBoundingClientRect();
			const viewportWidth = this.window.innerWidth;
			const viewportHeight = this.window.innerHeight;
			const margin = 8;

			let left = rect.left;
			let top = rect.top;
			let needsAdjustment = false;

			if (left < margin) {
				left = margin;
				needsAdjustment = true;
			} else if (left + rect.width > viewportWidth - margin) {
				left = Math.max(margin, viewportWidth - rect.width - margin);
				needsAdjustment = true;
			}

			if (top < margin) {
				top = margin;
				needsAdjustment = true;
			} else if (top + rect.height > viewportHeight - margin) {
				top = Math.max(margin, viewportHeight - rect.height - margin);
				needsAdjustment = true;
			}

			if (needsAdjustment) {
				const computedStyle = this.window.getComputedStyle(overlayElement);
				const matrix = computedStyle.transform;

				let currentX = 0;
				let currentY = 0;

				if (matrix && matrix !== 'none') {
					const matrixValues = matrix.match(/matrix\(([^)]+)\)/);
					if (matrixValues) {
						const values = matrixValues[1].split(',').map((v) => parseFloat(v.trim()));
						if (values.length >= 6) {
							currentX = values[4];
							currentY = values[5];
						}
					}
				}

				const deltaX = left - rect.left;
				const deltaY = top - rect.top;

				const newX = currentX + deltaX;
				const newY = currentY + deltaY;

				overlayElement.style.transform = `translate(${newX}px, ${newY}px)`;
			}

			if (this.showArrow && this.contentComponentRef) {
				const arrowPosition = this.contentComponentRef.instance.arrowPosition;
				this.calculateAndSetArrowOffset(this.contentComponentRef, arrowPosition);
			}
		});
	}
}
